import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Download, FileText, FileSpreadsheet, BarChart3, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) => `$${v.toLocaleString()}`;

const ExportReportDialog = ({ open, onOpenChange, claims }) => {
  const [exporting, setExporting] = useState(null);

  const activeClaims = claims.filter(c => c.status !== 'done');
  const doneClaims = claims.filter(c => c.status === 'done');
  const totalCharges = claims.reduce((s, c) => s + c.total, 0);
  const avgScore = Math.round(claims.reduce((s, c) => s + c.score, 0) / claims.length);

  const downloadCSV = () => {
    setExporting('csv');
    const headers = ['Claim ID', 'Patient', 'DOS', 'Payer', 'Provider', 'Total', 'Score', 'Status', 'Issues', 'Diagnoses', 'CPT Codes'];
    const rows = claims.map(c => [
      c.id, c.patient, c.dos, c.payer, c.provider, c.total, c.score + '%', c.status, c.issues,
      (c.dx || []).map(d => d.code).join('; '), (c.lines || []).map(l => l.cpt).join('; ')
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `claims_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setTimeout(() => { setExporting(null); toast.success('CSV exported'); }, 500);
  };

  const downloadJSON = () => {
    setExporting('json');
    const data = claims.map(c => ({
      id: c.id, patient: c.patient, dos: c.dos, payer: c.payer, provider: c.provider,
      total: c.total, score: c.score, status: c.status, issues: c.issues,
      diagnoses: c.dx, lines: c.lines, patientInfo: c.patientInfo,
      providerInfo: c.providerInfo, insuranceInfo: c.insuranceInfo
    }));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `claims_export_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setTimeout(() => { setExporting(null); toast.success('JSON exported'); }, 500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden" data-testid="export-report-dialog">
        <DialogHeader className="p-4 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Download className="h-4 w-4" /> Export & Reports
          </DialogTitle>
        </DialogHeader>

        <div className="p-4 space-y-4">
          {/* Daily summary */}
          <div style={{ padding: '12px 14px', borderRadius: 8, background: '#f9fafb', border: '0.5px solid #e8e8e8' }}>
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 style={{ width: 13, height: 13, color: '#3b82f6' }} />
              <span style={{ fontSize: 12, fontWeight: 500 }}>Daily Summary</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 18, fontWeight: 600, color: '#111' }}>{claims.length}</p>
                <p style={{ fontSize: 9, color: '#aaa' }}>Total Claims</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 18, fontWeight: 600, color: '#22c55e' }}>{doneClaims.length}</p>
                <p style={{ fontSize: 9, color: '#aaa' }}>Submitted</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 18, fontWeight: 600, color: '#f59e0b' }}>{activeClaims.length}</p>
                <p style={{ fontSize: 9, color: '#aaa' }}>Pending</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '0.5px solid #e8e8e8' }}>
              <span style={{ fontSize: 11, color: '#555' }}>Total charges: <strong>{fmt(totalCharges)}</strong></span>
              <span style={{ fontSize: 11, color: '#555' }}>Avg score: <strong>{avgScore}%</strong></span>
            </div>
          </div>

          {/* Export options */}
          <div className="space-y-2">
            <p style={{ fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase' }}>Export Format</p>
            <button onClick={downloadCSV} disabled={exporting === 'csv'} className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 transition-colors text-left" style={{ cursor: 'pointer', border: '0.5px solid #e8e8e8' }} data-testid="export-csv-btn">
              <FileSpreadsheet style={{ width: 16, height: 16, color: '#22c55e' }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 12, fontWeight: 500, color: '#111' }}>Export as CSV</p>
                <p style={{ fontSize: 10, color: '#888' }}>Spreadsheet-compatible format for Excel, Google Sheets</p>
              </div>
              {exporting === 'csv' ? <CheckCircle2 style={{ width: 14, height: 14, color: '#22c55e' }} /> : <Download style={{ width: 14, height: 14, color: '#aaa' }} />}
            </button>
            <button onClick={downloadJSON} disabled={exporting === 'json'} className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 transition-colors text-left" style={{ cursor: 'pointer', border: '0.5px solid #e8e8e8' }} data-testid="export-json-btn">
              <FileText style={{ width: 16, height: 16, color: '#3b82f6' }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 12, fontWeight: 500, color: '#111' }}>Export as JSON</p>
                <p style={{ fontSize: 10, color: '#888' }}>Structured data with all claim details and metadata</p>
              </div>
              {exporting === 'json' ? <CheckCircle2 style={{ width: 14, height: 14, color: '#22c55e' }} /> : <Download style={{ width: 14, height: 14, color: '#aaa' }} />}
            </button>
          </div>

          {/* Payer breakdown */}
          <div>
            <p style={{ fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', marginBottom: 6 }}>By Payer</p>
            <div className="space-y-1">
              {Object.entries(claims.reduce((acc, c) => { acc[c.payer] = (acc[c.payer] || { count: 0, total: 0 }); acc[c.payer].count++; acc[c.payer].total += c.total; return acc; }, {})).map(([payer, d]) => (
                <div key={payer} className="flex items-center justify-between" style={{ padding: '4px 8px', borderRadius: 4, background: '#fafafa' }}>
                  <span style={{ fontSize: 11, color: '#333' }}>{payer}</span>
                  <div className="flex items-center gap-2">
                    <Badge style={{ fontSize: 9, border: 'none', background: '#f0f0f0', color: '#888' }}>{d.count} claims</Badge>
                    <span style={{ fontSize: 11, fontWeight: 500, color: '#111' }}>{fmt(d.total)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportReportDialog;
